/**
 * Modal overlay shown when the state machine reaches RESULT.
 *
 * Step 5: renders the ShareCard preview canvas + a Share button. The button
 *   - tries the Web Share API with the rendered PNG (mobile native sheet);
 *   - falls back to text-only Web Share (some desktop browsers);
 *   - falls back to clipboard with a "Copied!" toast (universal).
 *
 * Reads outcome + score + streak + shareCardText + puzzle from gameStore.
 */
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useGameStore } from "@/store/gameStore";
import { useAuthStore } from "@/store/authStore";
import {
  ShareCard,
  type ShareCardAccent,
  type ShareCardData,
  type ShareCardHandle,
} from "@/components/ShareCard";
import { shareResult, type ShareStatus } from "@/lib/share";
import { api } from "@/api/client";
import { SignInModal } from "@/components/SignInModal";
import { recordAttempt, canRecordVideo } from "@/game/recorder";
import { recordAttemptVertical } from "@/game/verticalRecorder";
import type { Outcome, OutcomeDetail } from "@/game";
import { formatCountdown, msUntilNextPuzzle } from "@/lib/puzzleNumber";

// Outcome → i18n key. The actual translated string is resolved via t() in
// the component so language changes re-render correctly.
const HEADLINE_KEY: Record<Outcome, string> = {
  GOAL: "result.goal",
  SAVED: "result.saved",
  POST: "result.post",
  OVER: "result.over",
  WIDE: "result.wide",
  WALL: "result.wall",
  MISS: "result.wide",
};

const COLOUR: Record<Outcome, string> = {
  GOAL: "text-emerald-300",
  SAVED: "text-amber-300",
  POST: "text-amber-200",
  OVER: "text-rose-300",
  WIDE: "text-rose-300",
  WALL: "text-slate-300",
  MISS: "text-rose-300",
};

const ACCENT: Record<Outcome, ShareCardAccent> = {
  GOAL: "emerald",
  SAVED: "amber",
  POST: "amber",
  OVER: "rose",
  WIDE: "rose",
  WALL: "slate",
  MISS: "rose",
};

const SUBLINE: Partial<Record<OutcomeDetail, string>> = {
  top_bin: "Top bins!",
  top_centre: "Top of the net.",
  near_post: "Near post finish.",
  centre: "Right down the middle.",
  left_post: "Off the left post.",
  right_post: "Off the right post.",
  crossbar: "Off the crossbar.",
  wide_left: "Drifted left.",
  wide_right: "Drifted right.",
  dived_left: "Keeper dived left.",
  dived_right: "Keeper dived right.",
  wall_left: "Wall: left side.",
  wall_centre: "Hit the centre of the wall.",
  wall_right: "Wall: right side.",
  ground: "Bounced before the goal.",
};

interface BuildShareTextArgs {
  date: string;
  shareText: string;
  outcome: Outcome;
  score: number;
  streakCurrent: number | null;
  /** When present, appends a "Watch + beat me → https://www.curvle.world/c/<id>"
   *  challenge link so recipients land on the voyeur replay viewer. */
  attemptId: string | null;
}

function buildShareText(args: BuildShareTextArgs): string {
  const parts: string[] = [];
  parts.push(`Curvle ${args.date}`);
  if (args.shareText) parts.push(args.shareText);
  if (args.outcome === "GOAL" && args.score > 0) parts.push(`Score ${args.score}`);
  if (args.streakCurrent && args.streakCurrent > 1) parts.push(`\u{1F525}${args.streakCurrent}`);
  let head = parts.join(" · ");
  // Don't append the URL into the text body — navigator.share's `url:`
  // field already carries it and WhatsApp/Telegram/etc. append it
  // automatically. Adding it to text duplicates the link.
  if (args.attemptId) {
    head += `\n    Watch + beat my kick ↓`;
  }
  return head;
}

export function ResultOverlay() {
  const { t } = useTranslation();
  const state = useGameStore((s) => s.state);
  const outcome = useGameStore((s) => s.outcome);
  const detail = useGameStore((s) => s.outcomeDetail);
  const sim = useGameStore((s) => s.simResult);
  const scoreFromServer = useGameStore((s) => s.score);
  const streak = useGameStore((s) => s.streak);
  const shareCardText = useGameStore((s) => s.shareCardText);
  const attemptId = useGameStore((s) => s.attemptId);
  const input = useGameStore((s) => s.input);
  const keeperReveal = useGameStore((s) => s.keeperReveal);
  const puzzle = useGameStore((s) => s.puzzle);
  const submitPending = useGameStore((s) => s.submitPending);
  const submitError = useGameStore((s) => s.submitError);
  const reset = useGameStore((s) => s.reset);
  const userDisplayName = useAuthStore((s) => s.user?.displayName ?? null);
  const userCountryCode = useAuthStore((s) => s.user?.countryCode ?? "XX");
  const authStatus = useAuthStore((s) => s.status);
  const [signInOpen, setSignInOpen] = useState(false);
  const [founder, setFounder] = useState<{ remaining: number; limit: number } | null>(null);
  // Fetch the founder counter for the anonymous post-kick sign-in CTA so the
  // "N spots left" scarcity is live before the user opens the modal.
  useEffect(() => {
    if (authStatus !== "anonymous") return;
    api.founderStatus().then(setFounder).catch(() => undefined);
  }, [authStatus]);

  const cardRef = useRef<ShareCardHandle | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [sharing, setSharing] = useState(false);
  // Live countdown to the next 00:00 UTC puzzle drop. Updates every
  // second while the result overlay is visible.
  const [countdownMs, setCountdownMs] = useState(() => msUntilNextPuzzle());
  useEffect(() => {
    const id = window.setInterval(() => {
      setCountdownMs(msUntilNextPuzzle());
    }, 1000);
    return () => window.clearInterval(id);
  }, []);
  // Vertical-export progress. Distinct from `sharing` so users can still
  // hit the regular Share button while a 9:16 render is in progress.
  const [verticalPending, setVerticalPending] = useState(false);
  // Pre-recorded video blob. Populated by a background useEffect when we
  // enter RESULT state. Lets handleShare hand a ready Blob to navigator.share
  // immediately — important because Chrome's user-gesture window expires
  // before recordAttempt's ~3s wall-clock would complete inside an onClick.
  const [preparedVideo, setPreparedVideo] = useState<{ blob: Blob; mimeType: string } | null>(null);

  // Auto-clear the toast after a couple of seconds.
  useEffect(() => {
    if (!toast) return;
    const t = window.setTimeout(() => setToast(null), 2200);
    return () => window.clearTimeout(t);
  }, [toast]);

  // Defer the overlay reveal by 200ms after entering RESULT state so the
  // user gets a visible beat to see the keeper finish its dive before the
  // panel paints over the canvas. Closes audit P1-1 follow-up.
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    if (state === "RESULT") {
      const t = window.setTimeout(() => setVisible(true), 200);
      return () => window.clearTimeout(t);
    }
    setVisible(false);
    return undefined;
  }, [state]);

  // Pre-record the video in the background as soon as RESULT state lands.
  // This way handleShare can synchronously hand a Blob to navigator.share
  // and Chrome's user-gesture activation window stays intact. Without this
  // the recorder's ~3s wall-clock causes navigator.share to reject with
  // NotAllowedError and we fall through to clipboard-only.
  useEffect(() => {
    if (state !== "RESULT") {
      setPreparedVideo(null);
      return;
    }
    if (!puzzle || !input || !keeperReveal) return;
    if (!canRecordVideo()) return;
    let cancelled = false;
    (async () => {
      try {
        const rec = await recordAttempt({
          params: puzzle.params,
          aim: input,
          keeperReveal,
        });
        if (!cancelled) setPreparedVideo({ blob: rec.blob, mimeType: rec.mimeType });
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn("[curvle.share] background record failed; will use PNG", err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [state, puzzle, input, keeperReveal]);

  // Note: we no longer require `sim` to be non-null. The restore-existing-
  // attempt path (Pitch.tsx loads /api/attempt/today on mount and jumps
  // straight to RESULT) intentionally skips the replay step, so sim is
  // null there. Everything we need to render the overlay — outcome, score,
  // streak, share text — is mirrored into store top-level fields. Features
  // that genuinely need trajectory data (video recording, vertical clip)
  // gate on sim below.
  if (state !== "RESULT" || !outcome || !visible) return null;

  // Score lookup with fallback to scoreFromServer (mirrored in store when
  // restoreExistingAttempt runs). When sim is present its score wins —
  // they should be identical for normal flow.
  const score = sim?.score ?? scoreFromServer ?? 0;

  const headline = t(HEADLINE_KEY[outcome]);
  const colour = COLOUR[outcome];
  const subline = detail ? SUBLINE[detail] ?? null : null;
  const date = puzzle?.date ?? "";
  const stadium = puzzle?.stadiumName ?? null;
  const streakCurrent = streak ? streak.current : null;

  const cardData: ShareCardData = {
    shareText: shareCardText ?? "",
    headline,
    score: outcome === "GOAL" ? score : null,
    streak: streakCurrent,
    date,
    stadium,
    accent: ACCENT[outcome],
  };

  const handleShare = async () => {
    if (sharing) return;
    setSharing(true);
    try {
      // Pre-recorded video (best). If still preparing, fall back to PNG
      // synchronously so we don't await — Chrome's user-gesture window
      // for navigator.share is short and any pre-share await blows it.
      let blob: Blob | null = null;
      let filename = `curvle-${date || "today"}.png`;
      if (preparedVideo) {
        blob = preparedVideo.blob;
        const ext = preparedVideo.mimeType.includes("mp4") ? "mp4" : "webm";
        filename = `curvle-${date || "today"}.${ext}`;
      } else {
        // PNG fallback. toBlob is async but typically <50ms — well under
        // the gesture window. If video recording is still pending, the
        // user gets the PNG share card; if recording hasn't been kicked
        // off (recorder unsupported), same outcome.
        blob = (await cardRef.current?.toBlob()) ?? null;
      }
      const text = buildShareText({
        date,
        shareText: shareCardText ?? "",
        outcome,
        score: score ?? 0,
        streakCurrent,
        attemptId,
      });
      // Share URL points at api.curvle.world/c/<id> so unfurlers
      // (WhatsApp/Twitter/Slack/Telegram/Discord/iMessage) scrape its
      // OG meta tags and render the per-attempt PNG card. Humans hit
      // the api host first, see a sub-100ms JS redirect to
      // www.curvle.world/c/<id>, then the SPA loads as normal.
      const shareUrl = attemptId
        ? `https://api.curvle.world/c/${attemptId}`
        : undefined;
      // Personalized title for the OS share-sheet header. "<name> scored
      // <score> — beat me on Curvle" for goals; outcome-flavoured copy
      // for the rest. Falls back to "Curvle Player" when anonymous.
      const namePart = userDisplayName?.trim() || "A Curvle player";
      let shareTitle: string;
      switch (outcome) {
        case "GOAL":
          shareTitle = `${namePart} scored ${score} — beat me on Curvle`;
          break;
        case "SAVED":
          shareTitle = `${namePart}'s shot saved on Curvle — try yours`;
          break;
        case "POST":
          shareTitle = `${namePart} hit the post on Curvle — try yours`;
          break;
        case "WALL":
          shareTitle = `${namePart}'s shot blocked on Curvle — try yours`;
          break;
        case "OVER":
        case "WIDE":
          shareTitle = `${namePart}'s free kick missed — beat me on Curvle`;
          break;
        default:
          shareTitle = `${namePart} took today's Curvle — try yours`;
      }
      const status: ShareStatus = await shareResult({
        blob,
        text,
        filename,
        title: shareTitle,
        ...(shareUrl ? { url: shareUrl } : {}),
      });
      if (status === "shared") setToast("Shared!");
      else if (status === "copied") setToast("Copied to clipboard!");
      else setToast(null);
    } finally {
      setSharing(false);
    }
  };

  /**
   * Generate a 9:16 portrait clip + offer it to the user. We can't pre-record
   * this in the background like we do for the 16:9 share (the vertical render
   * runs ~5s including the end card, longer than the 16:9's ~3s, and only a
   * small minority of users will tap this button) — so it runs synchronously
   * on click and we surface progress.
   *
   * Mobile: navigator.share with the File so the OS share sheet offers
   *   TikTok / Instagram Stories+Reels / WhatsApp Status / Save to camera roll.
   * Desktop: download as .webm — TikTok desktop upload accepts webm and
   *   transcodes; Instagram desktop requires upload-via-phone anyway.
   */
  const handleVerticalShare = async () => {
    if (verticalPending || !puzzle || !input || !keeperReveal) return;
    setVerticalPending(true);
    setToast("Rendering vertical clip…");
    try {
      const challengeUrl = attemptId
        ? `https://api.curvle.world/c/${attemptId}`
        : "https://www.curvle.world";
      const namePart = userDisplayName?.trim() || "A Curvle player";
      const result = await recordAttemptVertical({
        params: puzzle.params,
        aim: input,
        keeperReveal,
        playerName: namePart,
        countryCode: userCountryCode,
        outcome,
        score: score ?? 0,
        challengeUrl,
      });
      const ext = result.mimeType.includes("mp4") ? "mp4" : "webm";
      const filename = `curvle-vertical-${date || "today"}.${ext}`;
      const file = new File([result.blob], filename, { type: result.mimeType });

      // Try Web Share API with file (mobile). canShare with files is the
      // gating capability — Chrome desktop reports it true but the OS
      // share sheet can fail to launch; we catch and fall back.
      const nav = navigator as Navigator & {
        canShare?: (data: ShareData) => boolean;
      };
      if (typeof nav.share === "function" && nav.canShare?.({ files: [file] })) {
        try {
          await nav.share({
            files: [file],
            title: "My Curvle kick",
            text: "Beat my score on Curvle",
            url: challengeUrl,
          });
          setToast("Shared!");
          return;
        } catch (err) {
          // AbortError = user dismissed, no-op. Anything else = fall back.
          if (err instanceof Error && err.name === "AbortError") {
            setToast(null);
            return;
          }
          // eslint-disable-next-line no-console
          console.warn("[curvle.vertical] share file failed; falling back to download", err);
        }
      }

      // Desktop fallback: trigger a download.
      const url = URL.createObjectURL(result.blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      // Revoke after a tick so the browser has time to start the save.
      window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
      setToast("Saved! Upload to TikTok/Reels.");
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn("[curvle.vertical] render failed", err);
      setToast("Couldn't render clip — try the regular Share.");
    } finally {
      setVerticalPending(false);
    }
  };

  const canShare = Boolean(shareCardText);
  // Vertical share rebuilds the kick from `input` + `keeperReveal` via
  // recordAttemptVertical. On the restore-existing-attempt path we don't
  // populate `input` (the user already played; they're seeing the result
  // panel cold). Without input we can't render the video, so hide the
  // button rather than letting it fail mid-render.
  const canVertical =
    Boolean(shareCardText) && canRecordVideo() && !!input && !!keeperReveal && !!puzzle;

  return (
    <div className="absolute inset-0 z-10 flex items-center justify-center bg-black/40 backdrop-blur-sm">
      <div className="w-[min(28rem,90%)] rounded-3xl bg-white/10 p-6 text-center backdrop-blur">
        <h2 className={`text-4xl font-bold ${colour}`}>{headline}</h2>
        {subline && <p className="mt-1 text-sm opacity-80">{subline}</p>}

        {/* Countdown to next 00:00 UTC puzzle drop. Helps players in any
            timezone see when tomorrow's kick lands in their local clock. */}
        <p className="mt-2 text-xs opacity-60">
          Next puzzle in <span className="font-mono">{formatCountdown(countdownMs)}</span>
        </p>

        <div className="mt-4">
          <ShareCard ref={cardRef} {...cardData} />
        </div>

        {streak && streak.current > 0 && (
          <p className="mt-3 text-sm opacity-80">
            {"\u{1F525}"} Streak: <span className="font-semibold">{streak.current}</span>
            {streak.longest > streak.current && (
              <span className="opacity-60"> &middot; best {streak.longest}</span>
            )}
          </p>
        )}

        {/* Post-kick sign-in prompt for anonymous players. Peak-motivation
            moment: streak just earned, score fresh. Pairs the live founder
            counter (scarcity) with streak-saving (loss aversion). Opens the
            existing SignInModal (Google OAuth + founder banner). */}
        {authStatus === "anonymous" && (
          <div className="mt-4 rounded-2xl bg-amber-300/15 p-4 text-center ring-1 ring-amber-300/40">
            <p className="text-sm font-semibold text-amber-100">
              {founder && founder.remaining > 0
                ? t("founder.signin_banner", {
                    limit: founder.limit.toLocaleString(),
                    remaining: founder.remaining.toLocaleString(),
                  })
                : t("result.signin_streak")}
            </p>
            <button
              type="button"
              className="mt-3 w-full rounded-full bg-amber-300 px-5 py-2.5 font-bold text-emerald-950 transition hover:bg-amber-200"
              onClick={() => setSignInOpen(true)}
            >
              {t("result.signin_cta")}
            </button>
          </div>
        )}

        {submitPending && <p className="mt-3 text-xs opacity-50">Saving&hellip;</p>}
        {submitError && (
          <p className="mt-3 text-xs text-amber-300">
            Couldn&rsquo;t save to server: {submitError}
          </p>
        )}

        <div className="mt-5 flex flex-wrap items-center justify-center gap-3">
          <button
            type="button"
            disabled={sharing || !canShare}
            className="rounded-full bg-white px-5 py-2 font-semibold text-emerald-900 transition hover:bg-white/90 disabled:opacity-50"
            onClick={handleShare}
          >
            {sharing ? t("result.sharing") : t("result.share")}
          </button>
          {canVertical && (
            <button
              type="button"
              disabled={verticalPending}
              className="rounded-full bg-amber-300 px-5 py-2 font-semibold text-emerald-950 transition hover:bg-amber-200 disabled:opacity-60"
              onClick={handleVerticalShare}
              title="Save a 9:16 vertical clip for TikTok / Reels / WhatsApp Status"
            >
              {verticalPending ? t("result.rendering") : t("result.tiktok_reels")}
            </button>
          )}
          <button
            type="button"
            className="rounded-full bg-white/10 px-5 py-2 font-semibold text-white transition hover:bg-white/20"
            onClick={() => reset()}
          >
            {t("result.try_again")}
          </button>
          <Link
            to="/leaderboard"
            className="rounded-full bg-white/10 px-5 py-2 font-semibold text-white transition hover:bg-white/20"
          >
            {t("result.leaderboard_cta")}
          </Link>
        </div>

        {/* Friend Leagues CTA — surfaced right after a kick when emotion is
            high and the "did I beat my friends?" question is most salient.
            This is Plan B-V4's primary discovery hook. */}
        <Link
          to="/leagues"
          className="mt-3 inline-flex items-center justify-center gap-2 rounded-full bg-amber-300/15 px-5 py-2 text-sm font-semibold text-amber-200 ring-1 ring-amber-300/40 transition hover:bg-amber-300/25"
        >
          <span aria-hidden="true">👥</span>
          <span>{t("result.challenge_friends")}</span>
        </Link>

        <p
          className="mt-3 h-5 text-sm text-emerald-300"
          aria-live="polite"
          role="status"
        >
          {toast ?? ""}
        </p>

        <SignInModal open={signInOpen} onClose={() => setSignInOpen(false)} />
      </div>
    </div>
  );
}
